import { useMemo } from "react";
import { RebuildAvatar } from "@/components/RebuildAvatar";
import type { SelectFieldOptions } from "./Field";
import { EstimateStatus } from "@rebuild/shared";
import { useUser } from "@/hooks/useUser";
import type { Estimation } from "@/interfaces/estimation.interface";
import { CircleUser, MapPin } from "lucide-react";
import { AddressField } from "./AddressField";
import { DateField } from "./DateField";
import { useStatusOptions } from "./estimate-status-options";
import { FieldDetail } from "./Field";
import type { SelectedTPAFields } from "../useTPARules";
import { useGetCarriersQuery, useGetTPAsQuery } from "@/hooks/queries/organization";
import { useEstimateDetail } from "@/hooks/useEstimateDetail";
import { useGetRateCardsQuery } from "@/hooks/queries/rate-cards";
import { EstimateType, ESTIMATE_TYPE_LABELS } from "@/enums/estimate-type";
import { MitigationCatClassFields } from "./MitigationCatClassFields";
import { CreatedByBadge } from "@/components/dashboard/CreatedByBadge";

interface GeneralDetailsProps {
  estimateId: string;
  estimation: Estimation | undefined;
  updateEstimationDetails: (_estimation: Estimation, field: keyof Estimation, clientOnly?: boolean) => Promise<boolean>;
  selectedTPAFields: SelectedTPAFields;
  updateSelectedTPAField: (field: keyof SelectedTPAFields, value: string) => void;
}

const sortByLabel = (a: SelectFieldOptions, b: SelectFieldOptions) => {
  const labelA = typeof a.label === "string" ? a.label : "";
  const labelB = typeof b.label === "string" ? b.label : "";
  return labelA.localeCompare(labelB);
};

export const GeneralDetails = ({
  estimateId,
  estimation,
  updateEstimationDetails,
  selectedTPAFields,
  updateSelectedTPAField,
}: GeneralDetailsProps) => {
  const { users, currentUser } = useUser();
  const rawStatusOptions = useStatusOptions();

  const statusOptions = useMemo(() => [...rawStatusOptions].sort(sortByLabel), [rawStatusOptions]);

  // Right now the estimation and estimateDetail objects in useEstimateDetail are automatically updated when the estimate is
  // finished loading, so we use that for checking these fields
  const { estimationDetail: _estimateDetail } = useEstimateDetail(estimateId);

  // we want to limit when the user can update fields like the tpa/carrier and cat/class while the estimate is still
  // generating; we have checks in the backend to make sure that these aren't updated when we don't want them to be
  // updated, but we do the same checks here so we can display helpful messages to the user
  const doesEstimateHaveLineItems = !!_estimateDetail?.length;

  const userOptions = useMemo<SelectFieldOptions[]>(
    () =>
      users
        .map((user) => {
          const initials = user.firstName.charAt(0).toUpperCase() + user.lastName.charAt(0).toUpperCase();
          return {
            label: `${user.firstName} ${user.lastName}`,
            value: user.lassoUserId,
            icon: <RebuildAvatar photo={user.photoURL} initials={initials} />,
          };
        })
        .sort(sortByLabel),
    [users],
  );

  const { data: carriers } = useGetCarriersQuery();
  const carrierOptions = carriers
    ?.map((carrier) => ({
      value: carrier.id,
      label: carrier.name,
    }))
    .sort(sortByLabel);

  const { data: tpas } = useGetTPAsQuery();
  const tpaOptions = tpas
    ?.map((tpa) => ({
      value: tpa.id,
      label: tpa.name,
    }))
    .sort(sortByLabel);

  const { data: rateCards } = useGetRateCardsQuery();
  const selectedRateCardName = useMemo(() => {
    if (!estimation?.rateCardId) {
      return undefined;
    }
    const card = rateCards?.find((c) => c.id === estimation.rateCardId);
    return card?.name;
  }, [rateCards, estimation?.rateCardId]);

  const officeOptions = useMemo<SelectFieldOptions[]>(
    () =>
      (currentUser?.extraData?.officeList ?? [])
        .filter((office) => office.length > 0)
        .map((office) => ({ label: office, value: office }))
        .sort(sortByLabel),
    [currentUser?.extraData?.officeList],
  );

  return (
    <div className="flex flex-col gap-4 py-2 pt-6 pr-10 pl-4">
      <FieldDetail
        estimation={estimation}
        label="Project"
        placeholder="Enter a project name"
        name={"projectName"}
        value={estimation?.projectName}
        updateEstimationDetails={updateEstimationDetails}
      />

      <div className="grid grid-cols-3">
        <div className="flex w-[100px] flex-col justify-center text-xs font-medium whitespace-nowrap text-[#71717A]">
          <label>Type</label>
        </div>
        <div className="col-span-2">
          <p className="text-sm">{estimation?.estimateType ? ESTIMATE_TYPE_LABELS[estimation.estimateType] : "-"}</p>
        </div>
      </div>

      {estimation?.rateCardId && (
        <div className="grid grid-cols-3">
          <div className="flex w-[100px] flex-col justify-center text-xs font-medium whitespace-nowrap text-[#71717A]">
            <label>Rate Card</label>
          </div>
          <div className="col-span-2">
            <p className="text-sm">{selectedRateCardName ?? "—"}</p>
          </div>
        </div>
      )}

      {estimation?.estimateType === EstimateType.MITIGATION && (
        <MitigationCatClassFields
          estimation={estimation}
          updateEstimationDetails={updateEstimationDetails}
          disabled={!doesEstimateHaveLineItems}
          disabledTooltip="Please wait until estimate is generated before updating the Cat/Class."
        />
      )}

      <FieldDetail
        estimation={estimation}
        label="Assignee"
        placeholder="Select Assignee"
        icon={<CircleUser className="h-4 w-4 text-[#9CA3AB]" />}
        name={"assignee"}
        options={userOptions}
        variant="select"
        value={estimation?.assignee}
        updateEstimationDetails={updateEstimationDetails}
      />

      <div className="grid grid-cols-3">
        <div className="flex w-[100px] flex-col justify-center text-xs font-medium whitespace-nowrap text-[#71717A]">
          <label>Created by</label>
        </div>
        <div className="col-span-2">
          <CreatedByBadge estimation={estimation} />
        </div>
      </div>

      <FieldDetail
        label="Status"
        placeholder="In Progress"
        icon={<img src="/in-progress.svg" className="size-4" />}
        estimation={estimation}
        value={estimation?.status ?? EstimateStatus.RECEIVED}
        name="status"
        options={statusOptions}
        variant="select"
        updateEstimationDetails={updateEstimationDetails}
      />

      <FieldDetail
        estimation={estimation}
        name="estimateReferenceNumber"
        label="Reference #"
        placeholder="123123123123"
        value={estimation?.estimateReferenceNumber}
        updateEstimationDetails={updateEstimationDetails}
      />

      <FieldDetail
        estimation={estimation}
        name="office"
        label="Office"
        placeholder="Select office"
        value={estimation?.office ?? ""}
        variant="select"
        options={officeOptions}
        updateEstimationDetails={updateEstimationDetails}
        comboboxShowClear
      />

      <FieldDetail
        estimation={estimation}
        name="carrierId"
        label="Carrier"
        placeholder="Select Carrier"
        value={selectedTPAFields.carrierId}
        variant="select"
        options={carrierOptions}
        updateEstimationDetails={async (estimation, field) => {
          updateSelectedTPAField("carrierId", estimation[field] as string);
          return true;
        }}
        disabled={!doesEstimateHaveLineItems}
        disabledTooltip="Please wait until estimate is generated before updating the carrier."
      />

      <FieldDetail
        estimation={estimation}
        name="tpaId"
        label="TPA"
        placeholder="Select TPA"
        icon={<img src="/tpa-insurance.svg" className="size-4" />}
        value={selectedTPAFields.tpaId}
        updateEstimationDetails={async (estimation, field) => {
          updateSelectedTPAField("tpaId", estimation[field] as string);
          return true;
        }}
        variant="select"
        options={tpaOptions}
        disabled={!doesEstimateHaveLineItems}
        disabledTooltip="Please wait until estimate is generated before updating the TPA."
      />

      <AddressField
        estimation={estimation}
        name="propertyAddress"
        label="Address"
        placeholder="Select Address"
        icon={<MapPin color="#9CA3AF" className="h-4 w-4" />}
        value={estimation?.propertyAddress}
        updateEstimationDetails={updateEstimationDetails}
      />

      <DateField
        estimation={estimation}
        name="dateOfLoss"
        label="Date loss"
        placeholder="M/D/YYYY"
        value={estimation?.dateOfLoss}
        updateEstimationDetails={updateEstimationDetails}
      />

      <DateField
        estimation={estimation}
        name="dateContacted"
        label="Date contacted"
        placeholder="M/D/YYYY"
        value={estimation?.dateContacted}
        updateEstimationDetails={updateEstimationDetails}
      />

    </div>
  );
};
